import type { TvChannel } from '@src/lib/graphql/generated';
import { getChannelLogo } from '@src/utils/sortedChannels';
import { composeStyles, Image, Link, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

export interface ChannelProps extends PropsWithClassProps<'image' | 'imageContainer'> {
  channel: TvChannel;
  showInitially?: boolean;
  onlyLogo?: boolean;
}

export const Channel = ({ channel, showInitially, onlyLogo, classProps }: ChannelProps) => {
  const channelElement = channel;

  const logoPath = useMemo(() => getChannelLogo(channel?.id), [channel.id]);
  const { ref: inViewRef, inView } = useInView({
    rootMargin: '50px 350px',
    triggerOnce: true,
  });

  const itemVisible = useMemo(() => inView || showInitially, [inView, showInitially]);

  if (!channelElement) return;

  const defaultStyle = {
    root: onlyLogo
      ? 'size-full'
      : 'streaming-entry-bg min-h-36 min-w-36 flex aspect-square w-full cursor-pointer items-center justify-center rounded-lg border-0.5 border-transparent p-2 text-center text-sm font-bold uppercase tracking-2px text-white transition-colors duration-150 hover:border-primary',
    image: 'h-auto max-h-full w-24 object-fit px-4 py-10',
    imageContainer: 'flex items-center',
  };

  const styles = composeStyles(defaultStyle, classProps);

  return (
    <Link
      noLink={onlyLogo}
      href={`/sender/${channelElement.slug || channelElement.id}`}
      classProps={{
        root: styles.root,
      }}
      ref={inViewRef}
    >
      {!itemVisible ? null : logoPath ? (
        <Image
          src={logoPath}
          alt={channel?.name}
          title={channel?.name}
          sizes="96px"
          fill
          copyright=""
          decoding="async"
          loading="lazy"
          classProps={{ root: styles.image, container: styles.imageContainer }}
        />
      ) : (
        <span className="hyphenate">{channel?.name}</span>
      )}
    </Link>
  );
};
